<!-- src/views/admin/vehicles/AdminVehicleEditorView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30">
    
    <!-- 1. PREMIUM HEADER CARD -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <TruckIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">
              {{ isEditing ? 'Refine Vehicle' : 'Initialise Vehicle' }}
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Fleet Asset Management & Driver Authorisation</p>
          </div>
          
          <button @click="$router.go(-1)" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Cancel Update</span>
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <form @submit.prevent="saveVehicle" class="space-y-12">
        
        <!-- SECTION 1: IDENTITY & VISUALS -->
        <div class="bg-white rounded-[40px] border border-slate-100 shadow-2xl overflow-hidden">
          <div class="relative bg-slate-900 h-64 flex items-center justify-center overflow-hidden">
            <img v-if="previewImage" :src="previewImage" class="w-full h-full object-cover opacity-60" alt="Vehicle Preview" />
            <div v-else class="text-center">
              <TruckIcon class="h-20 w-20 text-white/10 mx-auto" />
              <p class="micro-label text-slate-500 mt-4">No Asset Image Provided</p>
            </div>
            
            <label class="absolute bottom-6 right-6 bg-[#20FCF1] text-[#0A1630] px-6 py-3 rounded-xl shadow-xl text-[10px] font-black uppercase tracking-widest cursor-pointer hover:scale-105 transition-all">
              {{ previewImage ? 'Update Asset Photo' : 'Upload Asset Photo' }}
              <input type="file" class="hidden" accept="image/*" @change="handleImageUpload">
            </label>
          </div>

          <div class="p-10 space-y-12">
            <!-- CORE SPECS -->
            <section>
              <div class="flex items-center gap-3 mb-8">
                <div class="w-2 h-6 bg-[#20FCF1] rounded-full"></div>
                <h2 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em]">1. Technical Specifications</h2>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <label class="micro-label mb-2">Registration Number <span class="text-rose-500">*</span></label>
                  <input v-model="vehicle.registration" type="text" required class="form-input text-xl font-black uppercase tracking-widest" placeholder="KV17 HBG" />
                </div>
                <div>
                  <label class="micro-label mb-2">Manufacturer / Make <span class="text-rose-500">*</span></label>
                  <input v-model="vehicle.make" type="text" required class="form-input" placeholder="e.g. Vauxhall" />
                </div>
                <div>
                  <label class="micro-label mb-2">Specific Model <span class="text-rose-500">*</span></label>
                  <input v-model="vehicle.model" type="text" required class="form-input" placeholder="e.g. Vivaro" />
                </div>
                <div>
                  <label class="micro-label mb-2">Vehicle Classification</label>
                  <select v-model="vehicle.type" class="form-input font-bold uppercase">
                    <option value="Van">Van</option>
                    <option value="Car">Car</option>
                    <option value="Truck">Truck</option>
                    <option value="Pickup">Pickup</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label class="micro-label mb-2">Operational Status</label>
                  <select v-model="vehicle.status" class="form-input font-bold uppercase">
                    <option value="Active">Active (In Service)</option>
                    <option value="Maintenance">In Maintenance</option>
                    <option value="SORN">SORN (Off Road)</option>
                    <option value="Sold">Sold / Archived</option>
                  </select>
                </div>
                <div>
                  <label class="micro-label mb-2">Odometer Reading</label>
                  <div class="relative">
                    <input v-model.number="vehicle.mileage" type="number" class="form-input pr-12 font-mono" placeholder="0" />
                    <span class="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase tracking-widest">mi</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- DRIVER ALLOCATION -->
            <section class="bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner">
              <div class="flex items-center gap-3 mb-8">
                <div class="w-2 h-6 bg-[#0A1630] rounded-full"></div>
                <h2 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em]">2. Authorised Driver Allocation</h2>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label class="micro-label mb-2">Select Primary Driver</label>
                  <select v-model="vehicle.assignedUserId" class="form-input font-black uppercase tracking-tight" @change="updateDriverName">
                    <option :value="null">-- Unallocated / Pool Vehicle --</option>
                    <option v-for="user in users" :key="user.id" :value="user.id">
                      {{ user.firstName }} {{ user.lastName }}
                    </option>
                  </select>
                  <div class="mt-4 flex items-start gap-2 bg-[#EBFDF8] p-4 rounded-xl border border-[#20FCF1]/20">
                    <InformationCircleIcon class="h-4 w-4 text-[#20FCF1] mt-0.5" />
                    <p class="text-[9px] font-bold text-[#0A1630] uppercase tracking-widest leading-relaxed">
                      Allocating a driver will automatically synchronise this vehicle to their dashboard for compliance checks.
                    </p>
                  </div>
                </div>
                <div>
                  <label class="micro-label mb-2">Allocation Effective Date</label>
                  <input v-model="vehicle.dateAssigned" type="date" class="form-input" :disabled="!vehicle.assignedUserId" />
                </div>
              </div>
            </section>

            <!-- COMPLIANCE LOGS -->
            <section>
              <div class="flex items-center gap-3 mb-8">
                <div class="w-2 h-6 bg-rose-500 rounded-full"></div>
                <h2 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.3em]">3. Compliance & Expiry Protocols</h2>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <label class="micro-label mb-2 text-rose-500">MOT Expiry</label>
                  <input v-model="vehicle.motExpiry" type="date" class="form-input !bg-white" />
                </div>
                <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <label class="micro-label mb-2">Tax Renewal</label>
                  <input v-model="vehicle.taxRenewal" type="date" class="form-input !bg-white" />
                </div>
                <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <label class="micro-label mb-2">Next Service Due</label>
                  <input v-model="vehicle.serviceDue" type="date" class="form-input !bg-white" />
                </div>
                <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <label class="micro-label mb-2">Insurance Expiry</label>
                  <input v-model="vehicle.insuranceExpiry" type="date" class="form-input !bg-white" />
                </div>
              </div>
              
              <div class="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                 <div>
                  <label class="micro-label mb-2">Breakdown Assistance Provider</label>
                  <input v-model="vehicle.breakdownProvider" type="text" class="form-input" placeholder="e.g. AA, RAC, Green Flag" />
                </div>
                <div>
                  <label class="micro-label mb-2">Breakdown Contract / Policy No.</label>
                  <input v-model="vehicle.breakdownNumber" type="text" class="form-input font-mono" placeholder="POLICY_12345" />
                </div>
              </div>
            </section>
          </div>
        </div>

        <!-- FOOTER ACTIONS -->
        <div class="pt-10 flex flex-col sm:flex-row justify-between items-center gap-6">
          <button v-if="isEditing" type="button" @click="deleteVehicle" class="text-[10px] font-black text-rose-500 uppercase tracking-widest hover:underline">
            Permanent Decommission Asset
          </button>
          <div v-else></div>
          
          <div class="flex items-center gap-6">
            <button type="button" @click="$router.go(-1)" class="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-[#0A1630] transition-colors">Discard Draft</button>
            <button type="submit" :disabled="isSaving" class="bg-[#20FCF1] text-[#0A1630] px-12 py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 min-w-[240px]">
              <span v-if="isSaving" class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4 border-2 border-[#0A1630] border-t-transparent rounded-full" viewBox="0 0 24 24"></svg>
                Authorising Save...
              </span>
              <span v-else>Apply Fleet Update</span>
            </button>
          </div>
        </div>

      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { db, storage, auth } from '@/firebaseConfig';
import { collection, doc, getDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useUserStore } from '@/stores/userStore';
import { TruckIcon, ArrowUturnLeftIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const vehicleId = route.params.id === 'new' ? null : route.params.id;
const isEditing = computed(() => !!vehicleId);
const isSaving = ref(false);
const users = ref([]);

// Vehicle Data Model
const vehicle = reactive({
  registration: '',
  make: '',
  model: '',
  type: 'Van',
  status: 'Active',
  mileage: null,
  assignedUserId: null,
  assignedDriverName: '',
  dateAssigned: '',
  motExpiry: '',
  taxRenewal: '',
  serviceDue: '',
  insuranceExpiry: '',
  breakdownProvider: '',
  breakdownNumber: '',
  imageUrl: ''
});

const previewImage = ref('');
const imageFile = ref(null);

// Cache original driver to detect changes
let originalAssignedUserId = null;

const fetchUsers = async () => {
  try {
    const q = query(collection(db, 'userProfiles'), where('clientId', '==', userStore.clientId));
    const snap = await getDocs(q);
    users.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) { console.error("Error loading users", e); }
};

const fetchVehicle = async () => {
  if (!vehicleId) return;
  try {
    const docRef = doc(db, 'clients', userStore.clientId, 'vehicles', vehicleId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      Object.assign(vehicle, data);
      previewImage.value = data.imageUrl;
      originalAssignedUserId = data.assignedUserId;
    }
  } catch (e) { console.error("Error loading vehicle", e); }
};

const updateDriverName = () => {
  const user = users.value.find(u => u.id === vehicle.assignedUserId);
  vehicle.assignedDriverName = user ? `${user.firstName} ${user.lastName}` : '';
  if (vehicle.assignedUserId && !vehicle.dateAssigned) {
    vehicle.dateAssigned = new Date().toISOString().split('T')[0];
  }
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    imageFile.value = file;
    previewImage.value = URL.createObjectURL(file);
  }
};

/**
 * Standardised Promise wrapper for Storage Uploads
 */
const uploadVehicleImage = (file, vid, cid) => {
  return new Promise((resolve, reject) => {
    const path = `clients/${cid}/vehicles/${vid}/photo.jpg`;
    const fileRef = storageRef(storage, path);
    const metadata = { contentType: file.type || 'image/jpeg' };
    const uploadTask = uploadBytesResumable(fileRef, file, metadata);

    uploadTask.on('state_changed', 
      null, 
      (error) => reject(error), 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      }
    );
  });
};

const saveVehicle = async () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (!cid) {
    alert("System Authorisation Error: Client ID context missing.");
    return;
  }

  isSaving.value = true;
  const batch = writeBatch(db);
  
  try {
    // SECURITY REFRESH: Ensure auth token is fresh before high-impact write
    await auth.currentUser.getIdToken(true);

    const collectionRef = collection(db, 'clients', cid, 'vehicles');
    const docRef = isEditing.value ? doc(collectionRef, vehicleId) : doc(collectionRef);
    
    // 1. Handle Image Upload
    if (imageFile.value) {
      try {
        vehicle.imageUrl = await uploadVehicleImage(imageFile.value, docRef.id, cid);
      } catch (storageErr) {
        console.error("Storage Permission Rejection:", storageErr);
        throw new Error(`Storage access denied (${storageErr.code}). Check Firebase Storage Rules.`);
      }
    }

    const finalReg = (vehicle.registration || '').toUpperCase().trim();
    const vehicleData = { ...vehicle, registration: finalReg, id: docRef.id };

    // 2. Queue Vehicle record
    batch.set(docRef, vehicleData, { merge: true });

    // 3. Queue User Profile Synchronisation
    if (vehicle.assignedUserId !== originalAssignedUserId) {
        if (originalAssignedUserId) {
            batch.update(doc(db, 'userProfiles', originalAssignedUserId), { assignedVehicleReg: '' });
        }
        if (vehicle.assignedUserId) {
            batch.update(doc(db, 'userProfiles', vehicle.assignedUserId), { assignedVehicleReg: finalReg });
        }
    } else if (isEditing.value && originalAssignedUserId) {
        batch.update(doc(db, 'userProfiles', originalAssignedUserId), { assignedVehicleReg: finalReg });
    }

    await batch.commit();
    router.push({ name: 'AdminManageVehicles' });

  } catch (e) {
    console.error("Critical Registry Failure:", e);
    alert(`Save failure. Registry synchronisation could not be authorised.\n\nDetails: ${e.message}`);
  } finally {
    isSaving.value = false;
  }
};

const deleteVehicle = async () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (!confirm("Authorise decommissioning? This removes driver links.")) return;
  const batch = writeBatch(db);
  try {
    const docRef = doc(db, 'clients', cid, 'vehicles', vehicleId);
    if (vehicle.assignedUserId) {
        batch.update(doc(db, 'userProfiles', vehicle.assignedUserId), { assignedVehicleReg: '' });
    }
    batch.delete(docRef);
    await batch.commit();
    router.push({ name: 'AdminManageVehicles' });
  } catch (e) { alert("Decommissioning lock active."); }
};

onMounted(async () => {
  if (userStore.isAuthReady) {
    await fetchUsers();
    await fetchVehicle();
  }
});
</script>

<style scoped>
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.form-input { @apply mt-1 block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>